/**
 * Value helpers for rich-text surfaces migrating to canonical TipTap
 * JSON persistence (ADR-039).
 *
 * `<MarkdownEditor>` keeps a `value: string` prop boundary in BOTH
 * value formats — under `valueFormat='json'` the string is a
 * `JSON.stringify`'d ProseMirror doc. Rows on the wire, however, are
 * dual-format during the migration window: legacy rows carry a markdown
 * STRING, migrated rows carry a TipTap JSON OBJECT (plus a
 * `descriptionFormat` / `bodyFormat` discriminator). These helpers do
 * the (wire value ⇄ editor value-string ⇄ wire payload) conversions in
 * one place so every call site (card description, card comments, card
 * create) branches identically.
 */

import type { TipTapJsonContent } from '@/api/types-bridge'

/** A rich-text value as it appears on the wire during the migration window. */
export type RichTextWireValue = string | TipTapJsonContent

/** Per-row format discriminator the BE ships next to each body. */
export type RichTextWireFormat = 'markdown' | 'json'

/**
 * Does this editor value-string hold a serialized TipTap doc? A doc
 * always parses to an object carrying a string `type` (`'doc'` at the
 * root). Markdown prose essentially never does — and if a pathological
 * body did, the BE-side format column is what disambiguates on read;
 * this check only guards the WRITE path (deciding whether to send the
 * parsed object or the raw string).
 */
export function looksLikeSerializedDoc(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed.startsWith('{')) return false
  try {
    const parsed: unknown = JSON.parse(trimmed)
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { type?: unknown }).type === 'string'
    )
  } catch {
    return false
  }
}

/**
 * Convert an editor value-string into the wire payload value: the
 * parsed TipTap doc object when the string is a serialized doc (the
 * editor emitted JSON), otherwise the raw string (an untouched legacy
 * markdown draft — e.g. a no-op save on a markdown row). This is the
 * lazy write-coercion seam: any edit made through a `valueFormat='json'`
 * editor serializes as JSON, so saving an edited legacy card upgrades
 * the row.
 */
export function parseRichTextValue(value: string): RichTextWireValue {
  if (!looksLikeSerializedDoc(value)) return value
  return JSON.parse(value.trim()) as TipTapJsonContent
}

/**
 * Normalize a wire value (markdown string OR TipTap JSON object) into
 * the string `<MarkdownEditor value>` expects. Under
 * `valueFormat='json'` the editor parses JSON-looking strings back into
 * a doc and falls back to the markdown parser for everything else, so
 * one code path renders both row formats.
 */
export function richTextValueToEditorString(value: RichTextWireValue | null | undefined): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value)
  } catch {
    return ''
  }
}

/** Node types that carry no user-visible content on their own. */
const STRUCTURAL_NODE_TYPES = new Set(['doc', 'paragraph', 'text', 'hardBreak'])

interface LooseNode {
  type?: unknown
  text?: unknown
  content?: unknown
}

/** Depth-capped walk mirroring the BE validator's guard. */
function nodeHasContent(node: LooseNode, depth: number): boolean {
  if (depth > 128) return false
  if (typeof node.text === 'string' && node.text.trim().length > 0) return true
  if (typeof node.type === 'string' && !STRUCTURAL_NODE_TYPES.has(node.type)) {
    // Any non-structural node (mention, image, codeBlock, table, ...)
    // counts as content even without text — an image-only or
    // mention-only body is a legitimate submit.
    return true
  }
  if (Array.isArray(node.content)) {
    return node.content.some((child) => nodeHasContent(child as LooseNode, depth + 1))
  }
  return false
}

/**
 * Emptiness check that understands both value-string encodings. A
 * serialized empty doc (`{"type":"doc","content":[{"type":"paragraph"}]}`)
 * is non-empty as a STRING, so the old `value.trim().length === 0`
 * submit gates go through this instead.
 */
export function isRichTextValueEmpty(value: string): boolean {
  if (value.trim().length === 0) return true
  if (!looksLikeSerializedDoc(value)) return false
  return !nodeHasContent(JSON.parse(value.trim()) as LooseNode, 0)
}

/**
 * Return a copy of a TipTap doc with trailing empty paragraph nodes
 * removed. Chat sends on Enter, and ProseMirror splits the current
 * paragraph on that keypress before the composer's send handler runs,
 * so the live doc read at send time carries a trailing empty paragraph
 * that would render as a blank line. Empty trailing paragraphs are ALWAYS
 * this artifact — a multi-line chat message uses hardBreaks within one
 * paragraph (Shift+Enter), never separate empty paragraphs — so this is
 * safe. Leaves at least one node. Non-doc / stringy inputs pass through.
 */
export function stripTrailingEmptyParagraphs<T>(doc: T): T {
  if (doc === null || typeof doc !== 'object') return doc
  const d = doc as { type?: unknown; content?: unknown }
  if (!Array.isArray(d.content)) return doc
  const isEmptyParagraph = (n: unknown): boolean => {
    if (n === null || typeof n !== 'object') return false
    const node = n as { type?: unknown; content?: unknown }
    return node.type === 'paragraph' && (!Array.isArray(node.content) || node.content.length === 0)
  }
  const content = [...d.content]
  while (content.length > 1 && isEmptyParagraph(content[content.length - 1])) content.pop()
  return { ...d, content } as T
}

/**
 * Trim a TipTap doc's EDGES for submit-time hygiene: leading + trailing
 * paragraphs that are empty or whitespace-only are dropped, and the
 * first / last surviving text node loses its leading / trailing
 * whitespace — so a posted comment never opens or closes with blank
 * lines or stray spaces. Interior blank paragraphs are deliberate
 * authoring (paragraph breaks) and stay. Only paragraph edges are
 * touched: a doc that starts or ends with a non-paragraph block (image,
 * code block, table, …) keeps that block byte-identical. Trimming can
 * empty the doc entirely (a whitespace-only draft) — the result is the
 * canonical single-empty-paragraph doc; callers gate on emptiness
 * before submitting. Non-doc / stringy inputs pass through untouched.
 */
export function trimRichTextDocEdges<T>(doc: T): T {
  if (doc === null || typeof doc !== 'object') return doc
  const d = doc as { type?: unknown; content?: unknown }
  if (!Array.isArray(d.content)) return doc

  const isWhitespaceParagraph = (n: unknown): boolean => {
    if (n === null || typeof n !== 'object') return false
    const node = n as LooseWalkNode
    if (node.type !== 'paragraph') return false
    if (!Array.isArray(node.content) || node.content.length === 0) return true
    return node.content.every((child) => {
      const c = child as LooseWalkNode
      return c.type === 'text' && typeof c.text === 'string' && c.text.trim().length === 0
    })
  }

  const content = [...d.content]
  while (content.length > 0 && isWhitespaceParagraph(content[0])) content.shift()
  while (content.length > 0 && isWhitespaceParagraph(content[content.length - 1])) content.pop()

  // Whitespace off the first / last text node of the edge paragraphs.
  // Whitespace-only text nodes at the very edge are removed outright
  // (their neighbor becomes the new edge, so loop until a keeper).
  const trimParagraphEdge = (index: number, edge: 'start' | 'end'): void => {
    const node = content[index] as LooseWalkNode | undefined
    if (!node || node.type !== 'paragraph' || !Array.isArray(node.content)) return
    const children = [...node.content]
    for (;;) {
      const childIndex = edge === 'start' ? 0 : children.length - 1
      const child = children[childIndex] as LooseWalkNode | undefined
      if (!child || child.type !== 'text' || typeof child.text !== 'string') break
      const trimmed = edge === 'start' ? child.text.trimStart() : child.text.trimEnd()
      if (trimmed.length > 0) {
        if (trimmed !== child.text) children[childIndex] = { ...child, text: trimmed }
        break
      }
      children.splice(childIndex, 1)
    }
    content[index] = { ...node, content: children }
  }
  if (content.length > 0) trimParagraphEdge(0, 'start')
  if (content.length > 0) trimParagraphEdge(content.length - 1, 'end')

  if (content.length === 0) content.push({ type: 'paragraph' })
  return { ...d, content } as T
}

/**
 * True when an edit should NOT fire a mutation — the draft is visually
 * empty OR unchanged from the resolved original value-string. Edit
 * surfaces (chat message inline edit, and by convention any dual-format
 * body edit) gate on this so a no-op edit never stamps `editedAt` nor
 * silently upgrades a legacy markdown row's stored format: an untouched
 * editor never re-serializes, so its draft still equals the original.
 */
export function isNoopRichTextEdit(draft: string, resolvedOriginal: string): boolean {
  return isRichTextValueEmpty(draft) || draft.trim() === resolvedOriginal.trim()
}

interface LooseWalkNode {
  type?: unknown
  text?: unknown
  content?: unknown
  attrs?: Record<string, unknown>
}

/** Block-level node types that terminate a text line in the plain-text walk. */
const PLAIN_TEXT_LINE_BREAK_TYPES = new Set([
  'paragraph',
  'heading',
  'codeBlock',
  'blockquote',
  'listItem',
])

function attrString(attrs: Record<string, unknown> | undefined, key: string): string | null {
  const value = attrs?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function walkPlainText(node: LooseWalkNode, out: string[], depth: number): void {
  if (depth > 128) return
  const type = typeof node.type === 'string' ? node.type : ''
  if (typeof node.text === 'string') {
    out.push(node.text)
    return
  }
  switch (type) {
    case 'hardBreak':
      out.push('\n')
      return
    case 'mention': {
      // Label attr is stored WITHOUT the `@` sigil (the editor's
      // renderText adds it) — mirror that here. Defensively strip a
      // leading `@` so a sigil-bearing label can't render `@@name`,
      // matching the strip in <MessageRichText>.
      const raw = attrString(node.attrs, 'label') ?? attrString(node.attrs, 'id') ?? ''
      out.push(`@${raw.replace(/^@/, '')}`)
      return
    }
    case 'channelMention': {
      const label = attrString(node.attrs, 'label') ?? attrString(node.attrs, 'id') ?? ''
      out.push(`#${label}`)
      return
    }
    case 'wikilink': {
      const label = attrString(node.attrs, 'nodeName') ?? attrString(node.attrs, 'nodeId') ?? ''
      out.push(label)
      return
    }
    case 'workItemEmbed': {
      // `shorthand` is the node's only human-readable attr; the card's
      // title is resolved at render time and never denormalized. Without
      // this branch a chip contributes nothing to notification bodies or
      // the jumbo-emoji check, so "MOTE-30" would vanish from a bell row.
      out.push(attrString(node.attrs, 'shorthand') ?? '')
      return
    }
    default:
      break
  }
  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      walkPlainText(child as LooseWalkNode, out, depth + 1)
    }
  }
  if (PLAIN_TEXT_LINE_BREAK_TYPES.has(type)) out.push('\n')
}

/**
 * Derive a plain-text projection of a TipTap doc — for surfaces that
 * need prose without markup (OS notification bodies, delete-confirm
 * previews). Mentions render as `@Label`, channel mentions as `#Label`,
 * wikilinks as their cached display name; block boundaries become
 * newlines. The markdown analog is `stripMarkdown` in
 * `src/features/notifications/lib/strip-markdown.ts` — callers branch on
 * the wire body's runtime shape and pick one.
 */
export function richTextToPlainText(doc: TipTapJsonContent | null | undefined): string {
  if (doc == null) return ''
  const out: string[] = []
  walkPlainText(doc as LooseWalkNode, out, 0)
  return out
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Resolve a wire (value, format) pair into the editor value-string.
 * The format field wins when present; when absent (older BE responses —
 * rollout safety) the value's runtime shape discriminates: object ⇒
 * json, string ⇒ markdown.
 */
export function resolveRichTextEditorValue(
  value: RichTextWireValue | null | undefined,
  format?: RichTextWireFormat | null,
): { editorValue: string; format: RichTextWireFormat } {
  if (value == null) return { editorValue: '', format: format ?? 'markdown' }
  if (typeof value !== 'string') {
    return { editorValue: richTextValueToEditorString(value), format: 'json' }
  }
  return { editorValue: value, format: format ?? 'markdown' }
}
